// Per-context derivation of an AISummary shape.
//
// The matter context already has a fully-populated AISummary (either from
// the prototype fixture or the server). The other contexts (client, judge,
// opposing counsel, firm) don't have first-class summaries yet, so the
// AICard derives a best-effort summary from cross-matter
// signals available client-side. When there's truly no data, the helpers
// return null and the AICard renders an "insufficient data" empty state
// pointing at what would unlock the summary.

import type { AISummary, Matter } from "../../data/types";
import { matterList, mattersForClient } from "../../data/matters";

export interface DerivedSummary {
  summary: AISummary | null;
  /** When summary is null, this short message is shown as the empty state. */
  emptyMessage?: string;
  /** Source matter count — useful for the foot/transcript link. */
  sourceMatterCount?: number;
}

// ---------------------------------------------------------------------------
// Client — aggregate aiSummaries across all of this client's matters.
// Strategy: take the highest-confidence headline as the lede, union the top
// strengths/risks/next-best-actions across matters, dedupe and trim.
// ---------------------------------------------------------------------------

export function deriveClientSummary(clientId: string): DerivedSummary {
  const matters = mattersForClient(clientId).filter((m) => m.status !== "closed");
  const withSummary = matters.filter((m) => !!m.aiSummary?.headline);
  if (withSummary.length === 0) {
    return {
      summary: null,
      emptyMessage:
        matters.length === 0
          ? "AI summary will appear once this client has an active matter."
          : "AI summary will appear once one of this client's matters is analyzed.",
      sourceMatterCount: matters.length,
    };
  }

  // Highest-confidence matter sets the headline.
  const ranked = [...withSummary].sort(
    (a, b) => (b.aiSummary?.confidence ?? 0) - (a.aiSummary?.confidence ?? 0),
  );
  const top = ranked[0]!;

  const headline =
    withSummary.length === 1
      ? top.aiSummary!.headline
      : `Across ${withSummary.length} active matters: ${top.aiSummary!.headline}`;

  return {
    summary: {
      headline,
      confidence: avgConfidence(withSummary),
      strengths: collectTop(withSummary, "strengths", 5),
      risks: collectTop(withSummary, "risks", 5),
      nextBestActions: collectTop(withSummary, "nextBestActions", 5),
    },
    sourceMatterCount: withSummary.length,
  };
}

// ---------------------------------------------------------------------------
// Judge — derived from matters before this judge. Only fixture matters
// have judge linkage right now (we don't pull from the server query); when
// we have at least 3 matters with this judge we generate a profile, else
// we surface the insufficient-data message the prompt asked for.
// ---------------------------------------------------------------------------

export interface JudgeContext {
  judgeId: string;
  matters: Matter[];
}

export function deriveJudgeSummary(ctx: JudgeContext): DerivedSummary {
  const { matters } = ctx;
  if (matters.length < 3) {
    return {
      summary: null,
      emptyMessage: `Insufficient data — show me 3 matters before this judge to generate a profile (currently ${matters.length}).`,
      sourceMatterCount: matters.length,
    };
  }

  const practiceAreas = topCounts(matters.map((m) => m.practiceArea).filter(Boolean));
  const stages = topCounts(matters.map((m) => m.stage).filter(Boolean));

  return {
    summary: {
      headline: `${matters.length} matters before this judge — primarily ${practiceAreas.slice(0, 2).join(" / ")}`,
      confidence: 0.55, // derived, not first-class
      strengths: [
        `${matters.length} prior interactions across ${practiceAreas.length} practice area${practiceAreas.length === 1 ? "" : "s"}`,
        `Most-common stage: ${stages[0] ?? "—"}`,
      ],
      risks: [
        "Win-rate and time-to-decision averages need historical outcome data — not yet wired",
      ],
      nextBestActions: [
        "Pull 3 most-recent decisions from this judge for tone/standard signals",
        "Cross-reference with controlling-authority-check before next filing",
      ],
    },
    sourceMatterCount: matters.length,
  };
}

// ---------------------------------------------------------------------------
// Opposing counsel — same shape as judge but for matters opposite this
// counsel. Settle-vs-trial pattern would come from outcome data we don't
// have yet, so we surface that as a known gap.
// ---------------------------------------------------------------------------

export interface OpposingCounselContext {
  counselId: string;
  matters: Matter[];
}

export function deriveOpposingCounselSummary(ctx: OpposingCounselContext): DerivedSummary {
  const { matters } = ctx;
  if (matters.length < 3) {
    return {
      summary: null,
      emptyMessage: `Insufficient data — show me 3 matters opposite this counsel to generate a profile (currently ${matters.length}).`,
      sourceMatterCount: matters.length,
    };
  }

  const practiceAreas = topCounts(matters.map((m) => m.practiceArea).filter(Boolean));

  return {
    summary: {
      headline: `${matters.length} matters opposite this counsel — primarily ${practiceAreas.slice(0, 2).join(" / ")}`,
      confidence: 0.55,
      strengths: [`${matters.length} prior interactions on record`],
      risks: ["Settlement-vs-trial pattern needs outcome data — not yet wired"],
      nextBestActions: [
        "Pull settlement history from prior matters",
        "Note any standing motions/positions taken in past filings",
      ],
    },
    sourceMatterCount: matters.length,
  };
}

// ---------------------------------------------------------------------------
// Firm-wide — top-of-FirmCommandCenter summary derived from cross-matter
// signals available client-side. When the backend `/api/intelligence`
// endpoint returns a richer report (deadlines / missing docs / conflicts),
// the FirmCommandCenter wraps this with the server-derived strengths /
// risks via the optional `serverReport` injection. Pure client fallback
// is what's implemented here.
// ---------------------------------------------------------------------------

export function deriveFirmSummary(): DerivedSummary {
  const all = matterList().filter((m) => m.status !== "closed");
  if (all.length === 0) {
    return {
      summary: null,
      emptyMessage: "AI summary will appear once the firm has at least one active matter.",
      sourceMatterCount: 0,
    };
  }

  const critical = all.flatMap((m) => m.deadlines || []).filter((d) => d.bucket === "critical");
  const thisWeek = all.flatMap((m) => m.deadlines || []).filter((d) => d.bucket === "thisWeek");
  const needsAttention = all.filter((m) => m.needsAttention).length;
  const flaggedRisks = all
    .flatMap((m) => m.aiSummary?.risks ?? [])
    .filter(Boolean)
    .slice(0, 5);
  const missingCritical = all
    .flatMap((m) => m.missing || [])
    .filter((x) => x.priority === "critical");

  const risks: string[] = [];
  if (critical.length > 0) {
    risks.push(
      `${critical.length} matter${critical.length === 1 ? "" : "s"} with critical deadlines (≤3 days)`,
    );
  }
  if (missingCritical.length > 0) {
    risks.push(`${missingCritical.length} critical missing-document flags across the firm`);
  }
  if (flaggedRisks.length > 0) {
    risks.push(...flaggedRisks.slice(0, 3));
  }

  const strengths: string[] = [
    `${all.length} active matters across the firm`,
    `${all.length - needsAttention} clear of attention flags`,
  ];

  return {
    summary: {
      headline: `Across ${all.length} active matters: ${critical.length} critical, ${thisWeek.length} this week, ${needsAttention} flagged for attention`,
      confidence: 0.7,
      strengths,
      risks: risks.slice(0, 5),
      nextBestActions: [
        critical.length > 0 ? "Triage critical deadlines first (Deadline Radar)" : null,
        missingCritical.length > 0 ? "Resolve critical missing-doc flags" : null,
        "Run Priority Queue → Workspace Card for the next 3 actions",
      ].filter((x): x is string => !!x),
    },
    sourceMatterCount: all.length,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function avgConfidence(matters: Matter[]): number {
  const vals = matters.map((m) => m.aiSummary?.confidence ?? 0).filter((n) => n > 0);
  if (vals.length === 0) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function collectTop(
  matters: Matter[],
  key: "strengths" | "risks" | "nextBestActions",
  limit: number,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of matters) {
    const items = m.aiSummary?.[key] ?? [];
    for (const item of items) {
      const k = item.trim();
      if (!k || seen.has(k.toLowerCase())) continue;
      seen.add(k.toLowerCase());
      out.push(item);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

function topCounts(values: string[]): string[] {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k);
}
